import React, { useEffect, useCallback, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import * as _ from 'lodash';
import { MISSED_SESSIONS, DOWNLOAD_MISSED_SESSIONS } from 'actions/dataReport';
import { useSelector, useDispatch } from 'react-redux';
import AdminHeader from 'components/header';
import LayoutContainer from 'components/layout/LayoutContainer';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import { BsDownload, BsSearch } from 'react-icons/bs';
import { IoSearchOutline } from 'react-icons/io5';
import DateTimePicker from 'components/inputs/DateTimePicker';
import { BiSort } from 'react-icons/bi';
import fileDownload from 'js-file-download';
import ReactPagination from 'components/general/ReactPagination';
import SearchBox from 'components/general/SearchBox';
import { useTranslation } from 'react-i18next';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import Footer from 'components/general/Footer';
import { toast } from 'react-toastify';
import moment from 'moment';

const MissedSessions = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [sortByItem, setSortByItem] = useState({ item: '', order: '' });
  const [startDate, setStartDate] = useState(moment(new Date()).startOf('day'));
  const [endDate, setEndDate] = useState(moment(new Date()).endOf('day'));
  const [searchText, setSearchText] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);

  const missedSessionsList = useSelector((state) => state.missedSessions.missedSessions);
  const isLoader = useSelector((state) => state.missedSessions.isLoading);
  const page = useSelector((state) => state.missedSessions.page);
  const totalData = useSelector((state) => state.missedSessions.total);
  const limit = useSelector((state) => state.missedSessions.limit);
  const totalPages = useSelector((state) => state.missedSessions.totalPages);
  const isLoading = useSelector((state) => state.downloadingLoader.isLoading);
  const profileData = useSelector((state) => state.profile.userProfile);
  const currentUserName = _.get(profileData, 'name', '');

  const missedSessions = useCallback(
    (value = {}) => {
      const data = {
        ...value,
        from: moment(startDate).utc(),
        to: moment(endDate).utc(),
      };
      if (searchText) {
        data.search = searchText;
      }
      dispatch({
        type: MISSED_SESSIONS.REQUEST,
        payload: data,
      });
    },
    [startDate, endDate, searchText, dispatch]
  );

  const getSortByItem = useCallback(
    (name, order, page, filterDateData) => {
      const sortedOrder = order || 'asc';
      const sortByValue = `${sortedOrder === 'asc' ? '' : '-'}${name}`;

      const data = {
        from: moment(startDate).utc(),
        to: moment(endDate).utc(),
        sortBy: sortByValue,
      };

      if (searchText) {
        data.search = searchText;
      }

      if (page) {
        data.page = page.selected + 1;
      }

      if (filterDateData) {
        Object.assign(data, filterDateData);
      }

      missedSessions(data);
    },
    [startDate, endDate, searchText, missedSessions]
  );

  const dateFilter = useCallback(() => {
    if (sortByItem.item && sortByItem.order) {
      const filterDateData = {
        from: moment(startDate).utc(),
        to: moment(endDate).utc(),
      };
      getSortByItem(sortByItem.item, sortByItem.order, '', filterDateData);
    } else {
      const filterDateData = {
        from: moment(startDate).utc(),
        to: moment(endDate).utc(),
      };
      missedSessions(filterDateData);
    }
  }, [startDate, endDate, sortByItem, missedSessions, getSortByItem]);

  useEffect(() => {
    missedSessions();
  }, [missedSessions]);

  // Download Excel Functionality
  const downloadMissedSessions = useCallback(() => {
    const data = {
      excel: true,
      from: moment(startDate).utc(),
      to: moment(endDate).utc(),
    };
    if (searchText) {
      data.search = searchText;
    }
    dispatch({
      type: DOWNLOAD_MISSED_SESSIONS.REQUEST,
      payload: data,
      cb: (res) => {
        if (res) {
          if (_.get(res, 'status') === 200) {
            fileDownload(res.data, `Missed Sessions.xlsx`);
            toast.success('Excel downloaded successfully');
          }
        }
      },
    });
  }, [startDate, endDate, searchText, dispatch]);

  const downloadFile = () => {
    if (_.isEmpty(missedSessionsList) || isLoader) {
      toast.error('No data available to download');
      return;
    }
    downloadMissedSessions();
  };

  const handleSorting = (sortItem, dataItem) => {
    if (dataItem.item === sortItem) {
      const sortOrder = dataItem.order === 'asc' ? 'desc' : 'asc';
      setSortByItem({ item: sortItem, order: sortOrder });
      getSortByItem(sortItem, sortOrder);
    } else {
      setSortByItem({ item: sortItem, order: 'asc' });
      getSortByItem(sortItem, 'asc');
    }
  };

  const handlePageClick = useCallback(
    (page) => {
      if (sortByItem.item && sortByItem.order) {
        getSortByItem(sortByItem.item, sortByItem.order, page);
      } else if (startDate && endDate) {
        const data = {
          from: moment(startDate).utc(),
          to: moment(endDate).utc(),
          page: page.selected + 1,
        };
        if (searchText) {
          data.search = searchText;
        }
        missedSessions(data);
      } else {
        const data = {
          page: page.selected + 1,
        };
        if (searchText) {
          data.search = searchText;
        }
        missedSessions(data);
      }
    },
    [sortByItem, startDate, endDate, searchText, missedSessions, getSortByItem]
  );

  const searchHandler = (event) => {
    const value = _.get(event, 'target.value', '');
    setSearchText(value);
  };

  const handleSearch = () => {
    missedSessions({ page: 1 });
  };

  const search = (
    <SearchBox
      preIcon={<BsSearch />}
      value={searchText}
      onChange={searchHandler}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          handleSearch();
        }
      }}
    />
  );

  const searchIcon = (
    <IoSearchOutline
      size={27}
      onClick={() => setShowSearchBar(true)}
      className="report-search-icon"
    />
  );

  return (
    <>
      <LayoutContainer>
        <AdminHeader title="Missed Sessions" />
        <SkeletonTheme baseColor="#1b2a38" highlightColor="#15212b" enableAnimation="true">
          <div className="page-content-wrapper scrollable">
            <div className="data-report--main">
              <Card>
                <div className="data-report__inner">
                  <div className="data-report__box">
                    <Row className="data-report__row">
                      <Col
                        xl="auto"
                        md="auto"
                        className={showSearchBar ? '' : 'data-report-search-box'}
                      >
                        {showSearchBar ? search : searchIcon}
                      </Col>
                      <Col xl="auto" md="auto">
                        <div className="data-report-date_picker">
                          <DateTimePicker
                            onChangeOfStartDate={(item) => setStartDate(item)}
                            onChangeOfEndDate={(item) => setEndDate(item)}
                            initialValueOfStartDate={moment(new Date()).startOf('day')}
                            initialValueOfEndDate={moment(new Date()).endOf('day')}
                          />
                        </div>
                      </Col>
                      <Col xl="auto" md="auto">
                        <div className="report-search-box" onClick={dateFilter}>
                          <Button className="report-search-button">{t('button.search')}</Button>
                        </div>
                      </Col>
                      <Col xl="auto" md="auto" className="excel-icon--block">
                        {isLoading ? (
                          <div className="loader--block">
                            <Spinner as="span" animation="border" size="sm" role="status" />
                          </div>
                        ) : (
                          <div
                            className={`report-excel-icon ${_.isEmpty(missedSessionsList) || isLoader ? 'disabled' : ''}`}
                            onClick={_.isEmpty(missedSessionsList) || isLoader ? null : downloadFile}
                            style={_.isEmpty(missedSessionsList) || isLoader ? { opacity: 0.5, cursor: 'not-allowed' } : { cursor: 'pointer' }}
                            title={_.isEmpty(missedSessionsList) || isLoader ? 'No data available to download' : 'Download'}
                          >
                            <BsDownload size={28} color={_.isEmpty(missedSessionsList) || isLoader ? '#6c757d' : '#3c7cdd'} />
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                  <div className="data-report__table">
                    <div className="table-responsive">
                      <table className="record-list-table" id="table-to-xls">
                        <thead>
                          <tr>
                            <th>
                              <div className="sorting">
                                <span>{isLoader ? <Skeleton height={15} width={100} /> : t('dataReport.srNo')}</span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Booking ID'}</span>
                                <span className="ico" onClick={() => handleSorting('booking_id', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Charger ID'}</span>
                                <span className="ico" onClick={() => handleSorting('charger_id', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Initial Status'}</span>
                                <span className="ico" onClick={() => handleSorting('initialStatus', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>{isLoader ? <Skeleton height={15} width={100} /> : 'End Status'}</span>
                                <span className="ico" onClick={() => handleSorting('endStatus', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Initial Status Time'}</span>
                                <span className="ico" onClick={() => handleSorting('initialStatusTime', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>{isLoader ? <Skeleton height={15} width={100} /> : 'End Status Time'}</span>
                                <span className="ico" onClick={() => handleSorting('endStatusTime', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Authorization Status'}</span>
                                <span className="ico" onClick={() => handleSorting('authorizeStatus', sortByItem)}>
                                  <BiSort size={15} />
                                </span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Customer Name'}</span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Mobile No'}</span>
                              </div>
                            </th>
                            <th>
                              <div className="sorting">
                                <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Vehicle'}</span>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoader ? (
                            <tr>
                              <td colSpan={11} className="border-0">
                                <div className="loader--block">
                                  <Skeleton height={15} width={100} />
                                </div>
                              </td>
                            </tr>
                          ) : _.isEmpty(missedSessionsList) ? (
                            <tr>
                              <td colSpan={11} className="border-0">
                                <div className="empty-data-block">No missed sessions data found</div>
                              </td>
                            </tr>
                          ) : (
                            !(isLoader || _.isUndefined(isLoader)) &&
                            _.map(missedSessionsList, (item, key) => {
                              const serial_num = limit * (page - 1) + key;
                              const booking = item.booking || {};
                              const bookingId = booking?._id || booking?.id || '-';
                              const user = item.user || booking?.customer_user_booked || {};
                              const customerName = user?.name || '-';
                              const mobileNo = user?.phone || '-';
                              const vehicleInfo = booking?.vehicle;
                              const vehicleDisplay = vehicleInfo?.vin_num || vehicleInfo?.rto_no || '-';
                              const initialStatusTimeStr = item.initialStatusTime ? moment(item.initialStatusTime).format('DD/MM/YYYY, HH:mm:ss') : '-';
                              const endStatusTimeStr = item.endStatusTime ? moment(item.endStatusTime).format('DD/MM/YYYY, HH:mm:ss') : '-';

                              return (
                                <tr key={`missed-session-${serial_num}`}>
                                  <td>{serial_num + 1}</td>
                                  <td>{bookingId}</td>
                                  <td>{_.get(item, 'charger.charger_id', '-')}</td>
                                  <td>{item.initialStatus || '-'}</td>
                                  <td>{item.endStatus || '-'}</td>
                                  <td>{initialStatusTimeStr}</td>
                                  <td>{endStatusTimeStr}</td>
                                  <td>{item.authorizeStatus || 'No Authorization'}</td>
                                  <td>{customerName}</td>
                                  <td>{mobileNo}</td>
                                  <td>{vehicleDisplay}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {!(isLoader || _.isUndefined(isLoader)) && !_.isEmpty(missedSessionsList) && (
                    <ReactPagination
                      currentPage={page}
                      limit={limit}
                      total={totalData}
                      handlePageClick={(pageVal) => handlePageClick(pageVal)}
                      totalPages={totalPages}
                      marginPagesDisplayed={1}
                    />
                  )}
                </div>
              </Card>
            </div>
          </div>
        </SkeletonTheme>
        {currentUserName === 'CZ Tenant Admin' ? null : <Footer />}
      </LayoutContainer>
    </>
  );
};

export default MissedSessions;
